// src/components/research/ResearchMindmap.jsx
// Mindmap-Ansicht der Q&A-Fragen: Wurzel (Projekt) -> Kapitel -> Fragen ->
// Folgefragen. Nutzt exakt dieselbe Datenbasis wie die Listen-Ansicht
// (questionGroups/childrenByParent aus ResearchTool) und dieselbe
// moveQuestion-Semantik fürs Drag & Drop. Verbindungslinien werden nach jedem
// Render als SVG-Bezierpfade über die tatsächlichen DOM-Positionen gezeichnet
// (Ansatz aus der Referenz localFiles/local/SAKRAL_1.HTM, siehe ADR 0036).
// Klick auf Kapitel/Fragen mit Folgefragen klappt auf/zu; Doppelklick auf eine
// Frage öffnet die Inline-Bearbeitung (Fragetext + "+ Folgefrage" + Absprung in
// die Listen-Ansicht für die Detailfelder).

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatQuestionNo } from "../../services/azureResearchService";
import { snapshotMindmap } from "../../utils/mindmapPdf";
import ResearchMindmapPdfDialog from "./ResearchMindmapPdfDialog";
import ResearchMindmapLevelChips from "./ResearchMindmapLevelChips";

// Kapitel-Farben (zyklisch nach Kapitelreihenfolge), angelehnt an die Referenz.
const CHAPTER_COLORS = [
  "#4E7D3A",
  "#2E6DA4",
  "#C0662B",
  "#B8912A",
  "#5F6B7A",
  "#7A5AA6",
  "#3A7D74",
  "#A64D6D",
];
const UNASSIGNED_COLOR = "#6b7280";
const ROOT_COLOR = "#1F3864";

// Hex-Farbe mit Alpha (für abgetönte Rahmen/Balken der Fragen-Knoten).
const tint = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};

const ResearchMindmap = ({
  rootLabel,
  totalCount,
  questionGroups,
  childrenByParent,
  visibleSet,
  readOnly,
  moveQuestion,
  onRenameQuestion,
  onAddSubQuestion,
  onOpenInList,
}) => {
  // Zugeklappte Knoten: Key -> true ("root", "chap:<id|''>", "q:<id>").
  const [collapsed, setCollapsed] = useState({});
  // dragId doppelt geführt: State nur für die Optik (.dragging/.is-dragging),
  // Ref für die Event-Handler — die sind permanent montiert und dürfen nicht
  // von einem Re-Render während des Drags abhängen.
  const [dragId, setDragId] = useState(null);
  const dragIdRef = useRef(null);

  const filtering = !!visibleSet;
  const dndEnabled = !readOnly;

  // Inline-Bearbeitung des Fragetexts: der Entwurf lebt lokal, damit nicht bei
  // jedem Tastendruck die ganze Mindmap (inkl. Linien-Neuberechnung) neu rendert.
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  // Einfachklick klappt auf/zu, Doppelklick bearbeitet -> den Toggle kurz
  // verzögern und beim Doppelklick verwerfen (sonst klappt der Ast beim
  // Bearbeiten-Öffnen zweimal um).
  const clickTimerRef = useRef(null);

  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  // PDF-Export (ADR 0065): Snapshot der aktuell gerenderten Ansicht beim Öffnen.
  // Wechselt der Dialog die Ebene, wird nach dem nächsten Render neu vermessen.
  const [pdfSnapshot, setPdfSnapshot] = useState(null);
  const resnapRef = useRef(false);

  // Anzahl Nachfahren (Folgefragen im Teilbaum) je Frage.
  const subtreeSizes = useMemo(() => {
    const sizes = new Map();
    const calc = (id) => {
      if (sizes.has(id)) return sizes.get(id);
      sizes.set(id, 0); // Zyklus-Schutz
      const kids = childrenByParent.get(id) || [];
      const s = kids.reduce((acc, k) => acc + 1 + calc(k.id), 0);
      sizes.set(id, s);
      return s;
    };
    childrenByParent.forEach((_, pid) => {
      if (pid) calc(pid);
    });
    return sizes;
  }, [childrenByParent]);

  const toggle = useCallback((key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Ebenen (Tiefe): Wurzel 0, Kapitel 1, Unterkapitel und Fragen direkt am
  // Kapitel 2, Fragen im Unterkapitel 3, Folgefragen je +1. `keysByDepth` =
  // klappbare Knoten (mit Kindern) je Tiefe, für „Alle zuklappen“.
  const { keysByDepth, maxLevel } = useMemo(() => {
    const byDepth = [];
    let max = 0;
    const add = (depth, key, hasKids) => {
      max = Math.max(max, depth);
      if (!hasKids) return;
      (byDepth[depth] = byDepth[depth] || []).push(key);
    };
    const visit = (q, depth, seen) => {
      if (seen.has(q.id)) return; // Zyklus-Schutz
      seen.add(q.id);
      const kids = childrenByParent.get(q.id) || [];
      add(depth, `q:${q.id}`, kids.length > 0);
      kids.forEach((k) => visit(k, depth + 1, seen));
    };
    (questionGroups || []).forEach((g) => {
      const seen = new Set();
      add(1, `chap:${g.chapter ? g.chapter.id : ""}`, g.items.length > 0);
      (g.subgroups || [{ subchapter: null, items: g.items }]).forEach((sg) => {
        if (!sg.subchapter) {
          sg.items.forEach((q) => visit(q, 2, seen));
          return;
        }
        add(2, `sub:${sg.subchapter.id}`, sg.items.length > 0);
        sg.items.forEach((q) => visit(q, 3, seen));
      });
    });
    return { keysByDepth: byDepth, maxLevel: max };
  }, [questionGroups, childrenByParent]);

  // Ausgeblendete Ebenen (Chips): { [ebene]: true }. Unabhängig vom
  // Klapp-Zustand — eine ausgeblendete Ebene wird nicht gerendert, ihre Kinder
  // hängen am nächsten sichtbaren Vorfahren (Linien folgen automatisch dem DOM).
  const [hiddenLevels, setHiddenLevels] = useState({});
  const isLevelHidden = (depth) => !!hiddenLevels[depth];

  const levelStates = useMemo(
    () => Array.from({ length: maxLevel }, (_, i) => (hiddenLevels[i + 1] ? "off" : "on")),
    [maxLevel, hiddenLevels]
  );

  const toggleLevel = useCallback((n) => {
    setHiddenLevels((prev) => {
      const next = { ...prev };
      if (next[n]) delete next[n];
      else next[n] = true;
      return next;
    });
  }, []);
  const showAllLevels = useCallback(() => setHiddenLevels({}), []);
  const hideAllLevels = useCallback(() => {
    const next = {};
    for (let n = 1; n <= maxLevel; n += 1) next[n] = true;
    setHiddenLevels(next);
  }, [maxLevel]);

  // Aufklappen/Zuklappen bleibt davon getrennt. „Alle zuklappen“ klappt die
  // oberste sichtbare Ebene zu, sodass nur sie stehen bleibt.
  const expandAll = useCallback(() => setCollapsed({}), []);
  const collapseAll = useCallback(() => {
    let first = 1;
    while (first <= maxLevel && hiddenLevels[first]) first += 1;
    const next = {};
    (keysByDepth[first] || []).forEach((k) => (next[k] = true));
    setCollapsed(next);
  }, [keysByDepth, maxLevel, hiddenLevels]);
  const anyCollapsed = !filtering && Object.values(collapsed).some(Boolean);

  // --- Drag & Drop (gleiche Semantik wie die Listen-Ansicht) -----------------
  const endDrag = useCallback(() => {
    dragIdRef.current = null;
    setDragId(null);
  }, []);

  const handleDragStart = useCallback((ev, id) => {
    ev.dataTransfer.effectAllowed = "move";
    // Firefox startet ohne setData überhaupt keinen Drag.
    try {
      ev.dataTransfer.setData("text/plain", id);
    } catch {
      /* ältere Browser */
    }
    dragIdRef.current = id;
    // State-Update aus dem dragstart-Tick heraus verzögern: ein synchroner
    // Re-Render während dragstart bricht den Drag in Chromium sofort ab.
    window.setTimeout(() => {
      if (dragIdRef.current === id) setDragId(id);
    }, 0);
  }, []);

  // Vor eine Zielfrage: gleiche Ebene/Kapitel/Unterkapitel wie das Ziel, davor einsortiert.
  const handleDropBefore = useCallback(
    (target) => {
      const id = dragIdRef.current;
      if (id && id !== target.id) {
        moveQuestion(
          id,
          target.parentId || "",
          target.chapterId || "",
          target.subchapterId || "",
          target.id
        );
      }
      endDrag();
    },
    [moveQuestion, endDrag]
  );

  // Auf eine Frage: als Folgefrage ans Ende der Kinder; Ziel aufklappen.
  const handleDropOnto = useCallback(
    (targetId) => {
      const id = dragIdRef.current;
      if (id && id !== targetId) {
        moveQuestion(id, targetId, "", "", null);
        setCollapsed((prev) => ({ ...prev, [`q:${targetId}`]: false }));
      }
      endDrag();
    },
    [moveQuestion, endDrag]
  );

  // Auf ein Kapitel: oberste Ebene, ans Kapitelende (ohne Unterkapitel); aufklappen.
  const handleDropChapter = useCallback(
    (chapterId) => {
      const id = dragIdRef.current;
      if (id) {
        moveQuestion(id, "", chapterId, "", null);
        setCollapsed((prev) => ({ ...prev, [`chap:${chapterId}`]: false }));
      }
      endDrag();
    },
    [moveQuestion, endDrag]
  );

  // Auf ein Unterkapitel: ans Ende dieses Unterkapitels; aufklappen.
  const handleDropSubchapter = useCallback(
    (chapterId, subchapterId) => {
      const id = dragIdRef.current;
      if (id) {
        moveQuestion(id, "", chapterId, subchapterId, null);
        setCollapsed((prev) => ({ ...prev, [`sub:${subchapterId}`]: false }));
      }
      endDrag();
    },
    [moveQuestion, endDrag]
  );

  // --- Inline-Bearbeitung ----------------------------------------------------
  const clearClickTimer = useCallback(() => {
    if (clickTimerRef.current) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearClickTimer(), [clearClickTimer]);

  // Ausgangstext der offenen Bearbeitung — damit ein unveränderter Entwurf
  // keinen Speichervorgang (und keinen Changelog-Eintrag) auslöst.
  const originalRef = useRef("");

  const startEdit = useCallback((q) => {
    originalRef.current = q.question || "";
    setEditId(q.id);
    setDraft(q.question || "");
  }, []);

  // Entwurf übernehmen (nur bei echter Änderung) und Bearbeitung schliessen.
  const commitEdit = useCallback(async () => {
    const id = editId;
    if (!id) return;
    const text = draft;
    const changed = text !== originalRef.current;
    setEditId(null);
    originalRef.current = "";
    if (changed && onRenameQuestion) await onRenameQuestion(id, text);
  }, [editId, draft, onRenameQuestion]);

  const cancelEdit = useCallback(() => {
    setEditId(null);
    originalRef.current = "";
    setDraft("");
  }, []);

  // Einfachklick = auf-/zuklappen (verzögert), Doppelklick = bearbeiten.
  const handleQuestionClick = useCallback(
    (key, hasKids) => {
      clearClickTimer();
      if (!hasKids) return;
      clickTimerRef.current = window.setTimeout(() => {
        clickTimerRef.current = null;
        toggle(key);
      }, 220);
    },
    [clearClickTimer, toggle]
  );

  const handleQuestionDblClick = useCallback(
    (q) => {
      clearClickTimer();
      if (readOnly) return;
      startEdit(q);
    },
    [clearClickTimer, readOnly, startEdit]
  );

  // Folgefrage anlegen: laufenden Entwurf sichern, Ast aufklappen und direkt in
  // die Bearbeitung der neuen Frage springen.
  const handleAddSub = useCallback(
    async (parentId) => {
      await commitEdit();
      if (!onAddSubQuestion) return;
      const created = await onAddSubQuestion(parentId);
      if (!created) return;
      setCollapsed((prev) => ({ ...prev, [`q:${parentId}`]: false }));
      originalRef.current = created.question || "";
      setEditId(created.id);
      setDraft(created.question || "");
    },
    [commitEdit, onAddSubQuestion]
  );

  // --- Verbindungslinien ------------------------------------------------------
  // Nach jedem Render: für jeden sichtbaren Knoten Bezierpfad vom rechten Rand
  // des Eltern-Labels zum linken Rand jedes Kind-Labels (relativ zum Canvas).
  const drawLinks = useCallback(() => {
    const canvas = canvasRef.current;
    const svg = svgRef.current;
    if (!canvas || !svg) return;
    const cb = canvas.getBoundingClientRect();
    svg.setAttribute("width", canvas.scrollWidth);
    svg.setAttribute("height", canvas.scrollHeight);
    let d = "";
    canvas.querySelectorAll(".mindmap-node").forEach((n) => {
      if (n.classList.contains("collapsed")) return;
      const plbl = n.querySelector(":scope > .mindmap-lbl");
      const kids = n.querySelector(":scope > .mindmap-kids");
      if (!plbl || !kids) return;
      // Unter einem zugeklappten Vorfahren (display:none) hat das Label keine
      // Geometrie — sonst entstünden Pfade zu (0,0) relativ zum Viewport.
      if (plbl.getBoundingClientRect().width === 0) return;
      [...kids.children].forEach((cn) => {
        if (!cn.classList.contains("mindmap-node")) return;
        const clbl = cn.querySelector(":scope > .mindmap-lbl");
        if (!clbl) return;
        const a = plbl.getBoundingClientRect();
        const b = clbl.getBoundingClientRect();
        const x1 = a.right - cb.left;
        const y1 = a.top - cb.top + a.height / 2;
        const x2 = b.left - cb.left;
        const y2 = b.top - cb.top + b.height / 2;
        const mx = x1 + (x2 - x1) / 2;
        d += `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2} `;
      });
    });
    const path = svg.querySelector("path");
    if (path) path.setAttribute("d", d);
  }, []);

  // Ohne Dependency-Array: Linien hängen von den gerenderten DOM-Positionen ab,
  // nicht von einzelnen Props — daher nach jedem Render neu zeichnen.
  useLayoutEffect(() => {
    drawLinks();
    if (resnapRef.current && canvasRef.current) {
      resnapRef.current = false;
      setPdfSnapshot(snapshotMindmap(canvasRef.current));
    }
  });

  useEffect(() => {
    window.addEventListener("resize", drawLinks);
    const ro = new ResizeObserver(() => drawLinks());
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => {
      window.removeEventListener("resize", drawLinks);
      ro.disconnect();
    };
  }, [drawLinks]);

  // --- Rendering ---------------------------------------------------------------
  // Dünne Drop-Zone vor einem Geschwister-Knoten. Bewusst IMMER im DOM
  // (layout-neutral über negative Margins) und nur während eines Drags
  // maus-aktiv (pointer-events via .is-dragging auf dem Canvas): würde sie erst
  // beim Drag-Start eingefügt, bräche der strukturelle Re-Render den gerade
  // startenden Drag ab (Chromium).
  const renderDropBefore = (target) => (
    <div
      className="mindmap-drop-before"
      onDragOver={(ev) => {
        const id = dragIdRef.current;
        if (!id || id === target.id) return;
        ev.preventDefault();
        ev.currentTarget.classList.add("over");
      }}
      onDragLeave={(ev) => ev.currentTarget.classList.remove("over")}
      onDrop={(ev) => {
        if (!dragIdRef.current) return;
        ev.preventDefault();
        ev.stopPropagation();
        ev.currentTarget.classList.remove("over");
        handleDropBefore(target);
      }}
    />
  );

  // Eintrag in einer Kinder-Spalte: Drop-Zone (nur vor sichtbaren Knoten) + Knoten.
  const renderEntry = (q, color, counter, depth) => (
    <React.Fragment key={q.id}>
      {dndEnabled && !isLevelHidden(depth) && renderDropBefore(q)}
      {renderQuestion(q, color, counter, depth)}
    </React.Fragment>
  );

  // Fragen-Knoten rekursiv (DFS, laufende Nummer wie in der Liste).
  const renderQuestion = (q, color, counter, depth) => {
    if (visibleSet && !visibleSet.has(q.id)) return null;
    counter.n += 1;
    const nr = counter.n;
    const kids = childrenByParent.get(q.id) || [];
    // Ausgeblendete Ebene: Knoten selbst nicht rendern (die Nummer zählt
    // trotzdem mit, damit sie zur Liste passt), Kinder rücken eine Ebene hoch.
    // Ein Klapp-Zustand des ausgeblendeten Knotens wird dabei ignoriert.
    if (isLevelHidden(depth)) {
      return (
        <React.Fragment key={q.id}>
          {kids.map((child) => renderEntry(child, color, counter, depth + 1))}
        </React.Fragment>
      );
    }
    const key = `q:${q.id}`;
    const isCollapsed = !filtering && !!collapsed[key];
    const hasKids = kids.length > 0;
    const isDragging = dragId === q.id;
    const isEditing = editId === q.id;
    // Während der Bearbeitung: kein Drag, kein Klapp-Klick, kein Drop auf diesen Knoten.
    const rowDnd = dndEnabled && !isEditing;

    return (
      <div className={`mindmap-node${isCollapsed ? " collapsed" : ""}`} key={q.id}>
        <div
          className={`mindmap-lbl mindmap-q${hasKids && !isEditing ? " branch" : ""}${isDragging ? " dragging" : ""}${isEditing ? " editing" : ""}`}
          style={{ borderLeft: `4px solid ${tint(color, 0.85)}` }}
          draggable={rowDnd}
          onClick={isEditing ? undefined : () => handleQuestionClick(key, hasKids)}
          onDoubleClick={isEditing ? undefined : () => handleQuestionDblClick(q)}
          onDragStart={rowDnd ? (ev) => handleDragStart(ev, q.id) : undefined}
          onDragEnd={rowDnd ? endDrag : undefined}
          onDragOver={
            rowDnd
              ? (ev) => {
                  const id = dragIdRef.current;
                  if (!id || id === q.id) return;
                  ev.preventDefault();
                  ev.currentTarget.classList.add("drop-onto");
                }
              : undefined
          }
          onDragLeave={
            rowDnd
              ? (ev) => ev.currentTarget.classList.remove("drop-onto")
              : undefined
          }
          onDrop={
            rowDnd
              ? (ev) => {
                  const id = dragIdRef.current;
                  if (!id || id === q.id) return;
                  ev.preventDefault();
                  ev.stopPropagation();
                  ev.currentTarget.classList.remove("drop-onto");
                  handleDropOnto(q.id);
                }
              : undefined
          }
          title={
            isEditing
              ? undefined
              : dndEnabled
                ? "Doppelklick zum Bearbeiten · Ziehen zum Verschieben; ablegen auf einer Frage = Folgefrage"
                : undefined
          }
        >
          {hasKids && !isEditing && (
            <span className="mindmap-toggle">{isCollapsed ? "▶" : "▼"}</span>
          )}
          {/* Links vom Text im Rahmen: erst die feste ID, dann die laufende
              Nummer (die sich beim Umsortieren ändert). */}
          {formatQuestionNo(q.questionNo) && (
            <span className="mindmap-q-id">{formatQuestionNo(q.questionNo)}</span>
          )}
          <span className="mindmap-q-nr">{nr}</span>
          {isEditing ? (
            <span
              className="mindmap-edit"
              // Wegklicken übernimmt den Entwurf. Der Container-Blur (statt
              // input.onBlur) ist nötig, damit ein Klick auf "+ Folgefrage" /
              // "Details" nicht vorher den Editor abräumt und den Klick verschluckt.
              onBlur={(ev) => {
                if (ev.currentTarget.contains(ev.relatedTarget)) return;
                commitEdit();
              }}
            >
              <input
                className="mindmap-edit-input"
                value={draft}
                placeholder="Fragetext"
                autoFocus
                onChange={(ev) => setDraft(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") {
                    ev.preventDefault();
                    commitEdit();
                  } else if (ev.key === "Escape") {
                    ev.preventDefault();
                    cancelEdit();
                  }
                }}
              />
              <span
                className="mindmap-edit-tools"
                // Fokus im Eingabefeld halten: sonst hängt es vom Browser ab, ob
                // `relatedTarget` beim Blur den Button trägt — trägt es ihn nicht,
                // räumt der Container-Blur den Editor ab und schluckt den Klick.
                onMouseDown={(ev) => ev.preventDefault()}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleAddSub(q.id)}
                  title="Folgefrage zu dieser Frage anlegen"
                >
                  <span className="mindmap-edit-plus">+</span> Folgefrage
                </button>
                {onOpenInList && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={async () => {
                      await commitEdit();
                      onOpenInList(q.id);
                    }}
                    title="Antwort, Tags und Quellen in der Listen-Ansicht bearbeiten"
                  >
                    Details
                  </button>
                )}
                <button type="button" className="btn btn-primary btn-sm" onClick={commitEdit}>
                  Fertig
                </button>
              </span>
            </span>
          ) : (
            <span className="mindmap-cap">
              {q.question || <span className="mindmap-untitled">(Frage ohne Titel)</span>}
            </span>
          )}
          {hasKids && !isEditing && (
            <span className="mindmap-badge" style={{ background: tint(color, 0.85) }}>
              {subtreeSizes.get(q.id) || kids.length}
            </span>
          )}
        </div>
        <div className="mindmap-kids">
          {kids.map((child) => renderEntry(child, color, counter, depth + 1))}
        </div>
      </div>
    );
  };

  // Kapitel-Farbe nach Position in der Kapitelreihenfolge ("Nicht zugeordnet" grau).
  const chapterColor = (g, idx) =>
    g.chapter ? CHAPTER_COLORS[idx % CHAPTER_COLORS.length] : UNASSIGNED_COLOR;

  const rootCollapsed = !filtering && !!collapsed.root;
  const counter = { n: 0 };

  return (
    <div className="research-mindmap">
      <div className="mindmap-toolbar">
        <ResearchMindmapLevelChips
          states={levelStates}
          onToggle={toggleLevel}
          onAll={showAllLevels}
          onNone={hideAllLevels}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={expandAll} disabled={filtering}>
          Alle aufklappen
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={collapseAll}
          disabled={filtering}
          title="Oberste sichtbare Ebene zuklappen"
        >
          Alle zuklappen
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => {
            if (canvasRef.current) setPdfSnapshot(snapshotMindmap(canvasRef.current));
          }}
          title="Mindmap als PDF exportieren (ein Blatt oder Einzelblätter)"
        >
          PDF exportieren
        </button>
        <span className="mindmap-hint">
          {readOnly
            ? "Klick auf Kapitel/Fragen klappt Äste auf und zu."
            : "Klick klappt auf/zu · Doppelklick auf eine Frage bearbeitet sie und legt Folgefragen an · Drag & Drop verschiebt (auf Frage = Folgefrage, auf Kapitel = ans Kapitelende)."}
        </span>
      </div>
      <div className="mindmap-legend">
        {(questionGroups || []).map((g, idx) =>
          g.chapter ? (
            <span key={g.chapter.id}>
              <i className="mindmap-dot" style={{ background: chapterColor(g, idx) }} />
              {g.chapter.name}
            </span>
          ) : null
        )}
      </div>
      <div className="mindmap-stage">
        <div className={`mindmap-canvas${dragId ? " is-dragging" : ""}`} ref={canvasRef}>
          <svg className="mindmap-links" ref={svgRef} aria-hidden="true">
            <path d="" fill="none" />
          </svg>
          <div className={`mindmap-node mindmap-root-node${rootCollapsed ? " collapsed" : ""}`}>
            <div
              className="mindmap-lbl mindmap-root branch"
              style={{ background: ROOT_COLOR, borderColor: ROOT_COLOR }}
              onClick={() => toggle("root")}
            >
              <span className="mindmap-toggle">{rootCollapsed ? "▶" : "▼"}</span>
              <span className="mindmap-cap">
                {rootLabel} · {totalCount} Frage{totalCount === 1 ? "" : "n"}
              </span>
            </div>
            <div className="mindmap-kids">
              {(questionGroups || []).map((g, idx) => {
                const chId = g.chapter ? g.chapter.id : "";
                const color = chapterColor(g, idx);
                const key = `chap:${chId}`;
                const isCollapsed = !filtering && !!collapsed[key];
                // Bei aktiver Suche Kapitel ohne sichtbare Fragen ausblenden.
                const visibleTop = visibleSet
                  ? g.items.filter((q) => visibleSet.has(q.id))
                  : g.items;
                if (filtering && visibleTop.length === 0) return null;
                const count = g.items.reduce(
                  (acc, q) => acc + 1 + (subtreeSizes.get(q.id) || 0),
                  0
                );
                const chapKey = g.chapter ? g.chapter.id : "__none";
                // Inhalt des Kapitels (Unterkapitel + Fragen). Bei ausgeblendeter
                // Ebene 1 hängt er direkt an der Wurzel.
                const chapterKids = (g.subgroups || [{ subchapter: null, items: g.items }]).map((sg) => {
                  // Fragen ohne Unterkapitel hängen direkt am Kapitel.
                  if (!sg.subchapter) {
                    return sg.items.map((q) => renderEntry(q, color, counter, 2));
                  }
                  const visibleSub = visibleSet
                    ? sg.items.filter((q) => visibleSet.has(q.id))
                    : sg.items;
                  if (filtering && visibleSub.length === 0) return null;
                  const subItems = sg.items.map((q) => renderEntry(q, color, counter, 3));
                  if (isLevelHidden(2)) {
                    return <React.Fragment key={sg.subchapter.id}>{subItems}</React.Fragment>;
                  }
                  const subKey = `sub:${sg.subchapter.id}`;
                  const subCollapsed = !filtering && !!collapsed[subKey];
                  const subCount = sg.items.reduce(
                    (acc, q) => acc + 1 + (subtreeSizes.get(q.id) || 0),
                    0
                  );
                  return (
                    <div
                      className={`mindmap-node${subCollapsed ? " collapsed" : ""}`}
                      key={sg.subchapter.id}
                    >
                      <div
                        className="mindmap-lbl mindmap-sub branch"
                        style={{
                          background: tint(color, 0.13),
                          borderColor: tint(color, 0.55),
                        }}
                        onClick={() => toggle(subKey)}
                        onDragOver={
                          dndEnabled
                            ? (ev) => {
                                if (!dragIdRef.current) return;
                                ev.preventDefault();
                                ev.currentTarget.classList.add("drop-onto");
                              }
                            : undefined
                        }
                        onDragLeave={
                          dndEnabled
                            ? (ev) => ev.currentTarget.classList.remove("drop-onto")
                            : undefined
                        }
                        onDrop={
                          dndEnabled
                            ? (ev) => {
                                if (!dragIdRef.current) return;
                                ev.preventDefault();
                                ev.stopPropagation();
                                ev.currentTarget.classList.remove("drop-onto");
                                handleDropSubchapter(chId, sg.subchapter.id);
                              }
                            : undefined
                        }
                      >
                        <span className="mindmap-toggle">{subCollapsed ? "▶" : "▼"}</span>
                        <span className="mindmap-cap">{sg.subchapter.name}</span>
                        <span className="mindmap-badge" style={{ background: color }}>
                          {subCount}
                        </span>
                      </div>
                      <div className="mindmap-kids">{subItems}</div>
                    </div>
                  );
                });
                if (isLevelHidden(1)) {
                  return <React.Fragment key={chapKey}>{chapterKids}</React.Fragment>;
                }
                return (
                  <div
                    className={`mindmap-node${isCollapsed ? " collapsed" : ""}`}
                    key={chapKey}
                  >
                    <div
                      className="mindmap-lbl mindmap-chap branch"
                      style={{ background: color, borderColor: color }}
                      onClick={() => toggle(key)}
                      onDragOver={
                        dndEnabled
                          ? (ev) => {
                              if (!dragIdRef.current) return;
                              ev.preventDefault();
                              ev.currentTarget.classList.add("drop-onto");
                            }
                          : undefined
                      }
                      onDragLeave={
                        dndEnabled
                          ? (ev) => ev.currentTarget.classList.remove("drop-onto")
                          : undefined
                      }
                      onDrop={
                        dndEnabled
                          ? (ev) => {
                              if (!dragIdRef.current) return;
                              ev.preventDefault();
                              ev.stopPropagation();
                              ev.currentTarget.classList.remove("drop-onto");
                              handleDropChapter(chId);
                            }
                          : undefined
                      }
                    >
                      <span className="mindmap-toggle">{isCollapsed ? "▶" : "▼"}</span>
                      <span className="mindmap-cap">
                        {g.chapter ? g.chapter.name : "Nicht zugeordnet"}
                      </span>
                      <span className="mindmap-badge mindmap-badge--chap">{count}</span>
                    </div>
                    <div className="mindmap-kids">{chapterKids}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {pdfSnapshot && (
        <ResearchMindmapPdfDialog
          snapshot={pdfSnapshot}
          title={rootLabel}
          onClose={() => setPdfSnapshot(null)}
          levelStates={levelStates}
          anyCollapsed={anyCollapsed}
          filtering={filtering}
          // Jede Ebenen-Änderung im Dialog wirkt auf die Mindmap im Hintergrund,
          // die nach dem nächsten Render neu vermessen wird.
          onLevelAction={(action) => {
            resnapRef.current = true;
            if (action === "all") showAllLevels();
            else if (action === "none") hideAllLevels();
            else if (action === "expand") expandAll();
            else toggleLevel(action);
          }}
        />
      )}
    </div>
  );
};

export default ResearchMindmap;
